/**
 * Origem de uma cobrança: o atendimento (ou pedido) que a gerou.
 *
 * Resolvido no backend (`BillingService.resolveBillingOrigin`) e devolvido em
 * `billing.origin`. Fica aqui, e não dentro de uma página, porque tanto o
 * detalhe da cobrança (link "Abrir consulta") quanto o formulário (para onde
 * voltar depois de salvar) precisam do mesmo mapa — duplicá-lo faria as duas
 * telas divergirem no dia em que um tipo novo aparecer.
 */
import type { ServiceLabels } from '@/lib/form-state/util'

export interface BillingOrigin {
    type: 'CONSULTATION' | 'EXAM' | 'PROCEDURE' | 'TELECONSULTATION'
    id: number
    title?: string | null
    scheduledAt?: string | null
    status?: string
}

export const ORIGIN_ROUTES: Record<string, string> = {
    CONSULTATION: '/bookings/consultations',
    EXAM: '/bookings/exams',
    PROCEDURE: '/bookings/orders',
    TELECONSULTATION: '/bookings/teleconsultations',
}

/**
 * Artigo do rótulo, para montar frases sem concordância errada ("n{a} consulta",
 * "n{o} exame"). Separado do rótulo porque a mesma origem aparece com preposições
 * diferentes conforme a frase.
 */
export const ORIGIN_ARTICLE: Record<string, 'o' | 'a'> = {
    CONSULTATION: 'a',
    EXAM: 'o',
    PROCEDURE: 'o',
    TELECONSULTATION: 'a',
}

/**
 * Rótulos da saúde. Fora dela o vocabulário muda (Procedimento -> Pedido,
 * Consulta -> Avaliação): use `getOriginLabels(useServiceLabel())` na tela.
 */
export const ORIGIN_LABELS: Record<string, string> = {
    CONSULTATION: 'Consulta',
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
    TELECONSULTATION: 'Teleconsulta',
}

/** `ORIGIN_LABELS` traduzido para o segmento da empresa logada. */
export function getOriginLabels(labels: ServiceLabels): Record<string, string> {
    return {
        CONSULTATION: labels.Consultation,
        EXAM: labels.Exam,
        PROCEDURE: labels.Order,
        TELECONSULTATION: labels.Teleconsultation,
    }
}

/**
 * `ORIGIN_ARTICLE` acompanhando o rótulo traduzido — o gênero muda com ele.
 * Teleconsulta é feminina; fora da saúde o mesmo registro chama-se "atendimento
 * online", masculino, e o artigo fixo produzia "na atendimento online".
 */
export function getOriginArticles(
    labels: ServiceLabels
): Record<string, 'o' | 'a'> {
    return {
        ...ORIGIN_ARTICLE,
        TELECONSULTATION: labels.isHealth ? 'a' : 'o',
    }
}

/**
 * Caminho da tela de detalhe da origem, ou `null` quando não há origem ou o
 * tipo é desconhecido (cobrança avulsa, tipo novo ainda sem rota).
 */
export function billingOriginPath(
    origin: BillingOrigin | null | undefined
): string | null {
    if (!origin?.type || !origin.id) return null
    const base = ORIGIN_ROUTES[origin.type]
    return base ? `${base}/${origin.id}` : null
}

/**
 * Origem que é um ATENDIMENTO (agendamento), não um pedido da loja. É o que
 * decide se o formulário volta para o atendimento após salvar.
 */
export function isAppointmentOrigin(
    origin: BillingOrigin | null | undefined
): boolean {
    return (
        !!origin?.type &&
        ['CONSULTATION', 'EXAM', 'PROCEDURE', 'TELECONSULTATION'].includes(
            origin.type
        )
    )
}
